import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"

import { isAllBooksId } from "@/lib/all-books"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { TierColor } from "@/components/household/tier-badge"

import { fetchBook, fetchBooks } from "./api"

export interface TierLabel {
  value: number
  label: string
  color?: TierColor | null
}

export function useFetchBooks() {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["books"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return fetchBooks(context)
    },
    staleTime: 1000 * 60 * 10,
  })
}

export function useFetchBook(id: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  const isAllBooks = isAllBooksId(id)

  return useQuery({
    queryKey: ["book", id],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchBook(context, id)
    },
    staleTime: 1000 * 60 * 10,
    enabled: (options?.enabled ?? true) && !isAllBooks,
    refetchInterval: (query) => {
      const book = query.state.data
      if (book?.ai_generating_profile_at) return 3000
      return false
    },
  })
}

const EMPTY_TIER_LABELS: Record<number, string> = {}
const EMPTY_TIER_COLORS: Record<number, TierColor> = {}
const EMPTY_TIER_LABELS_FOR_DISPLAY: TierLabel[] = []

export function useTierLabels(bookId: string, enabled = true) {
  const { data: book } = useFetchBook(bookId, { enabled })

  const tierLabels = book?.tier_labels || EMPTY_TIER_LABELS
  const tierLabelsForDisplay = book?.tier_labels_for_display || EMPTY_TIER_LABELS_FOR_DISPLAY
  const tierColors = book?.tier_colors || EMPTY_TIER_COLORS

  const getTierLabel = useMemo(
    () => (tierValue: number | null | undefined) => {
      if (tierValue === null || tierValue === undefined) return null
      return tierLabels[tierValue] || null
    },
    [tierLabels]
  )
  const getTierColor = useMemo(
    () => (tierValue: number | null | undefined) => {
      if (tierValue === null || tierValue === undefined) return null
      return tierColors[tierValue] || null
    },
    [tierColors]
  )

  return {
    tierLabels,
    tierColors,
    tierLabelsForDisplay,
    getTierLabel,
    getTierColor,
  }
}

export function useTierLabelsMap() {
  const { data: user } = useFetchCurrentUser()

  return useMemo(() => {
    if (!user?.books) return {}

    return user.books.reduce(
      (map, book) => {
        map[book.id] = {
          tierLabels: book.tier_labels || {},
          tierColors: book.tier_colors || {},
          tierLabelsForDisplay: book.tier_labels_for_display || [],
        }
        return map
      },
      {} as Record<
        string,
        { tierLabels: Record<string, string>; tierColors: Record<string, TierColor>; tierLabelsForDisplay: TierLabel[] }
      >
    )
  }, [user?.books])
}
